import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import partnerService, { Partner, PartnerAnalytics } from '../services/partnerService';
import TokenManager from '../core/auth/TokenManager';

interface PartnerState {
  partner: Partner | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  analytics: PartnerAnalytics | null;
}

const initialState: PartnerState = {
  partner: localStorage.getItem('partner') ? JSON.parse(localStorage.getItem('partner')!) : null,
  isAuthenticated: TokenManager.hasValidToken(),
  isLoading: false,
  error: null,
  analytics: null
};

/**
 * Async thunk for sending OTP
 */
export const sendOTP = createAsyncThunk(
  'partner/sendOTP',
  async (phone: string, { rejectWithValue }) => {
    try {
      const response = await partnerService.sendLoginOTP(phone);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send OTP');
    }
  }
);

/**
 * Async thunk for verifying login OTP
 */
export const verifyLoginOTP = createAsyncThunk(
  'partner/verifyLoginOTP',
  async (credentials: { phone: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await partnerService.verifyLoginOTP(credentials);
      // Use TokenManager to store tokens securely
      TokenManager.setTokens({
        accessToken: response.token,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn || 900,
        tokenType: 'Bearer'
      });
      localStorage.setItem('partner', JSON.stringify(response.partner));
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'OTP verification failed');
    }
  }
);

/**
 * Async thunk for partner login
 */
export const loginPartner = createAsyncThunk(
  'partner/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await partnerService.loginPartner(credentials);
      // Use TokenManager to store tokens securely
      TokenManager.setTokens({
        accessToken: response.token,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn || 900,
        tokenType: 'Bearer'
      });
      localStorage.setItem('partner', JSON.stringify(response.partner));
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

/**
 * Async thunk for partner registration
 */
export const registerPartner = createAsyncThunk(
  'partner/register',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await partnerService.registerPartner(data);
      // Use TokenManager to store tokens securely
      TokenManager.setTokens({
        accessToken: response.token,
        refreshToken: response.refreshToken,
        expiresIn: response.expiresIn || 900,
        tokenType: 'Bearer'
      });
      if (response.partner) {
        localStorage.setItem('partner', JSON.stringify(response.partner));
      }
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Registration failed');
    }
  }
);

/**
 * Async thunk for fetching partner profile
 */
export const fetchPartnerProfile = createAsyncThunk(
  'partner/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const profile = await partnerService.getPartnerProfile();
      localStorage.setItem('partner', JSON.stringify(profile));
      return profile;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile');
    }
  }
);

/**
 * Async thunk for updating partner profile
 */
export const updatePartnerProfile = createAsyncThunk(
  'partner/updateProfile',
  async (data: Partial<Partner>, { rejectWithValue }) => {
    try {
      const response = await partnerService.updatePartnerProfile(data);
      localStorage.setItem('partner', JSON.stringify(response.profile));
      return response.profile;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile');
    }
  }
);

/**
 * Async thunk for verifying OTP
 */
export const verifyPartnerOTP = createAsyncThunk(
  'partner/verifyOTP',
  async (otp: string, { rejectWithValue }) => {
    try {
      const response = await partnerService.verifyPartnerOTP(otp);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'OTP verification failed');
    }
  }
);

/**
 * Async thunk for fetching partner analytics
 */
export const fetchPartnerAnalytics = createAsyncThunk(
  'partner/fetchAnalytics',
  async ({ startDate, endDate }: { startDate?: string; endDate?: string }, { rejectWithValue }) => {
    try {
      const analytics = await partnerService.getPartnerAnalytics(startDate, endDate);
      return analytics;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch analytics');
    }
  }
);

const partnerSlice = createSlice({
  name: 'partner',
  initialState,
  reducers: {
    logout: (state) => {
      state.partner = null;
      state.isAuthenticated = false;
      state.error = null;
      state.analytics = null;
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('partner');
      partnerService.logout();
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Send OTP
    builder
      .addCase(sendOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendOTP.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Verify Login OTP
    builder
      .addCase(verifyLoginOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyLoginOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.partner = action.payload.partner || null;
      })
      .addCase(verifyLoginOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Login
    builder
      .addCase(loginPartner.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginPartner.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.partner = action.payload.partner || null;
      })
      .addCase(loginPartner.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Register
    builder
      .addCase(registerPartner.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerPartner.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.partner = action.payload.partner || null;
      })
      .addCase(registerPartner.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Profile
    builder
      .addCase(fetchPartnerProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPartnerProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.partner = action.payload;
      })
      .addCase(fetchPartnerProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update Profile
    builder
      .addCase(updatePartnerProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePartnerProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.partner = action.payload;
      })
      .addCase(updatePartnerProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Verify OTP
    builder
      .addCase(verifyPartnerOTP.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyPartnerOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.partner) {
          state.partner.is_partner_verified = true;
          // Update localStorage
          localStorage.setItem('partner', JSON.stringify(state.partner));
        }
      })
      .addCase(verifyPartnerOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch Analytics
    builder
      .addCase(fetchPartnerAnalytics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPartnerAnalytics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchPartnerAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  }
});

export const { logout, clearError } = partnerSlice.actions;
export default partnerSlice.reducer;
